import React, { useMemo, useState } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { useToast } from '../../../lib/ToastContext'
import { useCodeGenerator } from '../hooks/useCodeGenerator'

const MODE_OPTIONS = [
  {
    id: 'new_product',
    label: 'New Product',
    description: 'Reserve a fresh SKU and matching GTIN-8 for a new Shopify product.'
  },
  {
    id: 'add_variant',
    label: 'Add Variant',
    description: 'Generate the next safe code for a variant using the current SKU or barcode.'
  }
]

const TYPE_OPTIONS = [
  { key: 'perfume', label: 'Perfume', typeCode: '9' },
  { key: 'ring', label: 'Ring', typeCode: '1' },
  { key: 'wedding_band', label: 'Wedding Band', typeCode: '1' },
  { key: 'pendant', label: 'Pendant', typeCode: '2' },
  { key: 'necklace', label: 'Necklace', typeCode: '3' },
  { key: 'chain', label: 'Chain', typeCode: '4' },
  { key: 'bracelet', label: 'Bracelet', typeCode: '5' },
  { key: 'earrings', label: 'Earrings', typeCode: '6' },
  { key: 'watch', label: 'Watch', typeCode: '7' },
  { key: 'masonic', label: 'Masonic', typeCode: '8' },
  { key: 'other', label: 'Other', typeCode: '9' }
]

function computeEan8(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length < 7) return ''
  const base = digits.slice(0, 7)
  const nums = base.split('').map(Number)
  const oddSum = nums[0] + nums[2] + nums[4] + nums[6]
  const evenSum = nums[1] + nums[3] + nums[5]
  const total = oddSum * 3 + evenSum
  const check = (10 - (total % 10)) % 10
  return `${base}${check}`
}

function normalizeReference(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 8)
}

function normalizeShortSku(value) {
  const digits = normalizeReference(value)
  if (/^[1-9]\d{4}$/.test(digits)) return digits
  if (/^17[1-9]\d{5}$/.test(digits) && computeEan8(digits.slice(0, 7)) === digits) {
    return digits.slice(2, 7)
  }
  return ''
}

function getTypeOptionByCode(typeCode) {
  return TYPE_OPTIONS.find((item) => item.typeCode === String(typeCode || '')) || TYPE_OPTIONS[TYPE_OPTIONS.length - 1]
}

async function copyText(value) {
  if (typeof navigator === 'undefined' || !navigator?.clipboard?.writeText) {
    throw new Error('Clipboard is not available in this browser')
  }
  await navigator.clipboard.writeText(String(value || ''))
}

const CodeGeneratorTab = ({ compactViewport = false }) => {
  const toast = useToast()
  const { generateCode, generating, error } = useCodeGenerator()
  const [mode, setMode] = useState('new_product')
  const [typeKey, setTypeKey] = useState('ring')
  const [existingReference, setExistingReference] = useState('')
  const [result, setResult] = useState(null)

  const selectedType = useMemo(
    () => TYPE_OPTIONS.find((item) => item.key === typeKey) || TYPE_OPTIONS[0],
    [typeKey]
  )
  const parsedExistingSku = useMemo(
    () => normalizeShortSku(existingReference),
    [existingReference]
  )
  const inferredType = useMemo(
    () => parsedExistingSku ? getTypeOptionByCode(parsedExistingSku[0]) : selectedType,
    [parsedExistingSku, selectedType]
  )
  const previewSku = useMemo(() => {
    if (mode === 'add_variant' && parsedExistingSku) {
      const nextSequence = Math.min(9999, (Number.parseInt(parsedExistingSku.slice(1), 10) || 0) + 1)
      return `${parsedExistingSku[0]}${String(nextSequence).padStart(4, '0')}`
    }
    return `${selectedType.typeCode}2345`
  }, [mode, parsedExistingSku, selectedType])
  const previewBarcode = useMemo(
    () => computeEan8(`17${previewSku}`),
    [previewSku]
  )

  const handleGenerate = async () => {
    if (mode === 'add_variant' && !parsedExistingSku) {
      toast.warning('Enter a valid current SKU or GTIN-8 barcode first')
      return
    }

    try {
      const next = await generateCode({
        mode,
        typeKey,
        existingReference: existingReference.trim()
      })
      if (next) {
        setResult(next)
        toast.success('SKU and GTIN-8 reserved')
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to generate code')
    }
  }

  const handleCopy = async (value, label) => {
    try {
      await copyText(value)
      toast.success(`${label} copied`)
    } catch (err) {
      toast.error(err?.message || `Failed to copy ${label.toLowerCase()}`)
    }
  }

  return (
    <div className={compactViewport ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 xl:grid-cols-[minmax(360px,1.2fr)_minmax(300px,0.8fr)] gap-4'}>
      <Card variant="solid" className="border border-border/60 shadow-elevation-1">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="Barcode" size={20} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">SKU / GTIN-8 Generator</h3>
            <p className="text-sm text-muted-foreground">
              Reserve a clean sequential SKU plus valid EAN-8 barcode before creating a Shopify product or adding a Shopify variant.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {MODE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setMode(option.id)}
              className={`text-left rounded-xl border p-3 transition-colors ${
                mode === option.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border/60 bg-card hover:border-primary/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${mode === option.id ? 'border-primary text-primary' : 'border-border/80 text-transparent'}`}>
                  <Icon name="Check" size={12} />
                </div>
                <p className="text-sm font-medium text-foreground">{option.label}</p>
              </div>
              <p className="text-xs text-muted-foreground">{option.description}</p>
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Item Type</label>
            <select
              value={typeKey}
              onChange={(e) => setTypeKey(e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label} - Type {option.typeCode}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              SKU format is <span className="font-mono text-foreground">TNNNN</span>. Barcode format is <span className="font-mono text-foreground">17 + SKU + checksum</span>.
            </p>
          </div>

          {mode === 'add_variant' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Current SKU or GTIN-8</label>
              <input
                type="text"
                value={existingReference}
                onChange={(e) => setExistingReference(normalizeReference(e.target.value))}
                placeholder="12345 or 17123457"
                className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Enter the current Shopify SKU or barcode. The generator will reserve the next safe code after it.
              </p>
            </div>
          )}

          <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
            <p className="text-xs font-medium text-foreground mb-2">Preview</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-card border border-border/60 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Preview SKU</p>
                <p className="font-mono text-foreground">{previewSku}</p>
              </div>
              <div className="rounded-lg bg-card border border-border/60 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Preview GTIN-8</p>
                <p className="font-mono text-foreground">{previewBarcode || 'Invalid'}</p>
              </div>
            </div>
            {mode === 'add_variant' && parsedExistingSku && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Existing code resolves to <span className="font-medium text-foreground">{parsedExistingSku}</span> ({inferredType.label}).
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              onClick={handleGenerate}
              disabled={generating}
            >
              <Icon name={generating ? 'Loader2' : 'Sparkles'} size={14} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate & Reserve'}
            </Button>
            {result?.sku && (
              <Button
                variant="outline"
                onClick={() => handleCopy(result.sku, 'SKU')}
              >
                <Icon name="Copy" size={14} className="mr-2" />
                Copy SKU
              </Button>
            )}
            {result?.barcode && (
              <Button
                variant="outline"
                onClick={() => handleCopy(result.barcode, 'GTIN-8')}
              >
                <Icon name="Copy" size={14} className="mr-2" />
                Copy GTIN-8
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <Card variant="solid" className="border border-border/60 shadow-elevation-1">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-secondary/60 text-foreground flex items-center justify-center">
              <Icon name="FileDigit" size={18} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Latest Reserved Code</h3>
              <p className="text-xs text-muted-foreground">
                Use these values in Shopify when creating the product or variant.
              </p>
            </div>
          </div>

          {result ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">SKU</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-lg text-foreground">{result.sku}</p>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => handleCopy(result.sku, 'SKU')}
                  >
                    <Icon name="Copy" size={16} />
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">GTIN-8</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-lg text-foreground">{result.barcode}</p>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => handleCopy(result.barcode, 'GTIN-8')}
                  >
                    <Icon name="Copy" size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg border border-border/60 px-3 py-2">
                  <p className="text-muted-foreground mb-1">Type</p>
                  <p className="text-foreground font-medium">{result.typeLabel} ({result.typeCode})</p>
                </div>
                <div className="rounded-lg border border-border/60 px-3 py-2">
                  <p className="text-muted-foreground mb-1">Checksum</p>
                  <p className="text-foreground font-medium">{result.checksum}</p>
                </div>
                <div className="rounded-lg border border-border/60 px-3 py-2 col-span-2">
                  <p className="text-muted-foreground mb-1">Barcode Base</p>
                  <p className="text-foreground font-mono">{result.barcodeBase}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 p-4 text-sm text-muted-foreground">
              Generate a code to see the reserved SKU and GTIN-8 here.
            </div>
          )}
        </Card>

        <Card variant="solid" className="border border-border/60 shadow-elevation-1">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-warning/15 text-warning flex items-center justify-center">
              <Icon name="Info" size={18} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">How This Works</h3>
              <p className="text-xs text-muted-foreground">
                The last barcode digit is a checksum, so the app calculates it automatically.
              </p>
            </div>
          </div>

          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
              <span>SKU format: first digit is the item type, next four digits are the reserved sequence.</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
              <span>Barcode format: <span className="font-mono text-foreground">17 + SKU + checksum</span>.</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
              <span>Variant mode accepts the current SKU or current barcode, then reserves the next safe code.</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
              <span>Reservations prevent future generated codes from overlapping the ones you already took.</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  )
}

export default CodeGeneratorTab
